/**
 * Company → product → client → member tree shared by Explorer and Map.
 * Asset and infrastructure rows are loaded when a node is opened, not here.
 */
import { listCompanies, listAssetTeams, listProductClients, getAssetTeam } from './api';

export async function loadAssetHierarchy() {
  const [companies, products] = await Promise.all([listCompanies(), listAssetTeams()]);
  const withTeam = products.filter((p) => p.team_id);
  const detail = await Promise.all(withTeam.map((p) => Promise.all([
    listProductClients(p.product_id).catch(() => []),
    getAssetTeam(p.team_id).catch(() => null),
  ]).then(([clients, team]) => [p.product_id, { clients, team }])));
  const detailByProductId = Object.fromEntries(detail);

  const memberNode = (m, teamId, parentKey) => ({
    id: `member:${teamId}:${parentKey}:${m.user_id}`,
    type: 'member',
    name: m.name,
    teamId,
    userId: m.user_id,
    raw: m,
    filterClientId: parentKey !== 'direct' ? parentKey : null,
  });

  const productNode = (p) => {
    if (!p.team_id) {
      return {
        id: `product:${p.product_id}`,
        type: 'product',
        name: p.name,
        raw: p,
        hasTeam: false,
      };
    }
    const d = detailByProductId[p.product_id] || {};
    const members = d.team?.members || [];
    const clients = d.clients || [];
    const clientNodes = clients.map((c) => ({
      id: `client:${p.product_id}:${c.client_id}`,
      type: 'client',
      name: c.name,
      clientId: c.client_id,
      productId: p.product_id,
      teamId: p.team_id,
      raw: c,
      children: members
        .filter((m) => (m.client_ids || []).includes(c.client_id))
        .map((m) => memberNode(m, p.team_id, c.client_id)),
    }));
    const directMembers = members
      .filter((m) => !(m.client_ids || []).length)
      .map((m) => memberNode(m, p.team_id, 'direct'));
    return {
      id: `product:${p.team_id}`,
      type: 'product',
      name: p.name,
      raw: p,
      hasTeam: true,
      children: [...clientNodes, ...directMembers],
    };
  };

  const byCompany = {};
  withTeam.concat(products.filter((p) => !p.team_id)).forEach((p) => {
    const key = p.company_id || 'unassigned';
    (byCompany[key] = byCompany[key] || []).push(p);
  });

  const companyNodes = companies.map((c) => ({
    id: `company:${c.company_id}`,
    type: 'company',
    name: c.name,
    raw: c,
    children: (byCompany[c.company_id] || []).map(productNode),
  }));
  const unassigned = byCompany.unassigned || [];
  if (unassigned.length) {
    companyNodes.push({
      id: 'company:unassigned',
      type: 'company',
      name: 'Unassigned',
      raw: null,
      children: unassigned.map(productNode),
    });
  }
  return companyNodes;
}
